import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { WechatNativeImage } from './FileItem/Image'
import type { PreviewFile } from './types'

const Scroller = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    touch-action: auto;
`

const Slide = styled.div`
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    scroll-snap-align: center;
    scroll-snap-stop: always;
    display: flex;
    align-items: center;
    justify-content: center;
`

const ImageWrap = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`

interface WechatNativeImageGalleryProps {
    fileList: PreviewFile[]
    defaultIndex: number
    onSlideChange: (index: number) => void
}

/**
 * 微信内不用 Embla / use-gesture，避免 preventDefault、pointer capture 拦截原生长按菜单。
 * 多图左右滑改用系统滚动 + scroll-snap。
 */
export const WechatNativeImageGallery = ({ fileList, defaultIndex, onSlideChange }: WechatNativeImageGalleryProps) => {
    const scrollerRef = useRef<HTMLDivElement>(null)
    const indexRef = useRef(defaultIndex)

    useEffect(() => {
        const scroller = scrollerRef.current
        if (!scroller) {
            return
        }
        scroller.scrollTo({ left: scroller.clientWidth * defaultIndex, behavior: 'auto' })
        indexRef.current = defaultIndex
    }, [defaultIndex])

    return (
        <Scroller
            ref={scrollerRef}
            onScroll={() => {
                const scroller = scrollerRef.current
                if (!scroller) {
                    return
                }
                const nextIndex = Math.round(scroller.scrollLeft / Math.max(scroller.clientWidth, 1))
                if (nextIndex !== indexRef.current) {
                    indexRef.current = nextIndex
                    onSlideChange(nextIndex)
                }
            }}
        >
            {fileList.map((file, index) => (
                <Slide key={file.url || index}>
                    <ImageWrap>
                        <WechatNativeImage src={file.url} alt={file.name} />
                    </ImageWrap>
                </Slide>
            ))}
        </Scroller>
    )
}
